import React, {
  DragEvent,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import cn from "classnames";
import { ApolloError } from "@apollo/client";
import { add, formatDistanceToNowStrict } from "date-fns";
import styles from "./InputFile.module.css";

import Link from "next/link";
import { IconFiles } from "src/components/ui-components/Icons/IconFiles";
import { IconFileCSV } from "src/components/ui-components/Icons/IconFileCSV";
import { useSession } from "src/components/session/session";
import { useActiveContext } from "shared/ContextSwitcher/store";
import { IconButton } from "src/components/ui-components/Button/IconButton";
import { Warning as IconWarning } from "phosphor-react";
import { IconCross } from "src/components/ui-components/Icons/IconCross";
import { DataUploadAborted, ProgressData, uploadTable } from "./upload";
import { Notification } from "src/components/ui-components/Notification";
import { AnchorButton } from "src/components/ui-components/Button/AnchorButton";
import { useStorageUsage } from "src/hooks/useStorageUsage";

const MEGABYTE_IN_BYTES = 1024 * 1024;
const formatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 3,
});

const Progress: FC<{ percentage: number }> = ({ percentage }) => (
  <div className={styles.progressWrapper}>
    <div className={styles.progressBar}>
      <span
        className={styles.progressBarFill}
        style={{
          width: percentage.toLocaleString("en", {
            style: "percent",
          }),
        }}
      ></span>
    </div>
  </div>
);

type InputFilePickerProps = {
  onFileChosen: (file: File) => void;
  hasError: boolean;
  disabled?: boolean;
};

const InputFilePicker: FC<InputFilePickerProps> = ({
  onFileChosen,
  hasError,
  disabled,
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const onFileInput = useCallback(
    (files: FileList | null) => {
      const file = files?.[0];
      if (file) {
        onFileChosen(file);
      }
    },
    [onFileChosen]
  );

  const onFileDropped = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (ref.current) {
        ref.current.files = event.dataTransfer.files;
        onFileInput(event.dataTransfer.files);
      }
      event.preventDefault();
    },
    [onFileInput]
  );

  return (
    <>
      <div
        className={cn(
          styles.inputContainer,
          hasError && styles.errorContainer,
          disabled === true && styles.disabledContainer
        )}
        onDragOver={(event) => event.preventDefault()}
        onDragEnter={(event) => event.preventDefault()}
        onDrop={onFileDropped}
      >
        <div className={styles.iconContainer}>
          <IconFiles />
        </div>
        <div className={styles.inputDescriptionContainer}>
          <div>
            <p>
              Drag and drop a file,
              <label htmlFor="upload-table"> browse your files</label>, or
              upload data programmatically via{" "}
              <label>
                <Link
                  href="/docs/api/api-reference/upload-data/"
                  target="_blank"
                >
                  API
                </Link>
              </label>
              .
            </p>
          </div>
          <p>Max 200MB each. Files must be in CSV format.</p>
        </div>
      </div>
      <input
        ref={ref}
        className={styles.inputFile}
        type="file"
        id="upload-table"
        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        onChange={(event) => onFileInput(event.target.files)}
      />
    </>
  );
};

type InputFileUploaderProps = {
  file: File;
  progress: ProgressData | undefined;
  onRemoveFile: () => void;
  dataUploadProcessing: boolean;
  hasError: boolean;
};
const InputFileUploader: FC<InputFileUploaderProps> = ({
  file,
  progress,
  onRemoveFile,
  dataUploadProcessing,
  hasError,
}) => {
  const { name, size } = file;
  const { percentage, secondsRemaining, completed } = progress ?? {
    completed: false,
  };
  const notCompleted = !completed;

  let secondsDisplay: ReactNode;
  if (notCompleted && secondsRemaining !== undefined) {
    const formattedSeconds = formatDistanceToNowStrict(
      add(new Date(), { seconds: secondsRemaining }),
      { unit: secondsRemaining < 60 ? "second" : undefined }
    );
    secondsDisplay = ` • ${formattedSeconds} left`;
  }

  let percentDisplay: ReactNode;
  let progressDisplay: ReactNode;
  if (notCompleted && percentage !== undefined) {
    percentDisplay = (
      <p className={styles.percentage}>
        {percentage.toLocaleString("en", {
          style: "percent",
        })}
      </p>
    );
    progressDisplay = <Progress percentage={percentage} />;
  }

  return (
    <div>
      <div
        className={cn(
          styles.tableDataContainer,
          hasError && styles.errorContainer
        )}
      >
        <div className={styles.tableIconContainer}>
          <IconFileCSV />
        </div>
        <div className={styles.tableDescriptionContainer}>
          <section>
            <div className={styles.descriptionItem}>
              <h3>{name}</h3>
              {!dataUploadProcessing && (
                <IconButton
                  theme="tertiary"
                  size="S"
                  type="button"
                  onClick={() => {
                    onRemoveFile();
                  }}
                >
                  <IconCross />
                </IconButton>
              )}
            </div>
          </section>
          <section>
            <div className={styles.descriptionItem}>
              <p>
                {size > MEGABYTE_IN_BYTES
                  ? `${formatter.format(size / MEGABYTE_IN_BYTES)}MB`
                  : `${formatter.format(size / 1024)}KB`}
                {secondsDisplay}
              </p>
              {percentDisplay}
            </div>
            {progressDisplay}
          </section>
        </div>
      </div>
    </div>
  );
};

type InputFileProps = {
  dataUploadProcessing: boolean;
  onFileUploaded?: (file: File) => void;
  onFileRemoved?: () => void;
};
export const InputFile: FC<InputFileProps> = ({
  dataUploadProcessing,
  onFileUploaded,
  onFileRemoved,
}) => {
  const session = useSession();
  const activeContext = useActiveContext();

  const { isStorageLimitReached, isLoading: isLoadingStorageUsage } =
    useStorageUsage(activeContext);

  const [file, setFile] = useState<File | undefined>();
  const [progressData, setProgressData] = useState<ProgressData>();
  const [error, setError] = useState<string | undefined>();
  const [upgradeNotification, setUpgradeNotification] = useState<
    string | undefined
  >();

  useEffect(() => {
    if (isStorageLimitReached && upgradeNotification === undefined) {
      setUpgradeNotification(
        "You are above your storage limit. Upgrade plan to increase your storage limit and re-enable data upload."
      );
    }
  }, [isStorageLimitReached, upgradeNotification]);

  const onRemoveFile = useCallback(() => {
    progressData?.abort();
    setFile(undefined);
    setError(undefined);
    setUpgradeNotification(undefined);
    onFileRemoved?.();
  }, [onFileRemoved]);

  const onFileInput = useCallback(
    async (newFile: File) => {
      if (session && activeContext && newFile.type === "text/csv") {
        setError(undefined);
        setUpgradeNotification(undefined);
        setFile(newFile);

        await uploadTable(session, activeContext, newFile, setProgressData)
          .then(() => onFileUploaded?.(newFile))
          .catch((error) => {
            onRemoveFile();
            if (error instanceof DataUploadAborted) {
              setError("Data Upload Aborted");
            } else if (error instanceof ApolloError) {
              if (
                error.graphQLErrors[0]?.extensions?.key ===
                "data_upload_quota_exceeded"
              ) {
                setUpgradeNotification(
                  "Upgrade your plan to upload larger datasets"
                );
                setError(
                  "This dataset exceeds the available quota in your plan"
                );
              }
            } else {
              throw error;
            }
          });
      }
    },
    [onFileUploaded]
  );

  return (
    <div>
      {file !== undefined ? (
        <InputFileUploader
          file={file}
          progress={progressData}
          onRemoveFile={onRemoveFile}
          hasError={Boolean(error)}
          dataUploadProcessing={dataUploadProcessing}
        />
      ) : (
        <InputFilePicker
          onFileChosen={onFileInput}
          hasError={Boolean(error)}
          disabled={isLoadingStorageUsage || isStorageLimitReached}
        />
      )}
      {error !== undefined && (
        <div className={styles.error} role="alert">
          <IconWarning />
          {error}
        </div>
      )}

      {upgradeNotification !== undefined && (
        <div className={styles.upgradeNotification}>
          <Notification
            level="brand-orange"
            title={upgradeNotification}
            actions={
              <AnchorButton href="/pricing" size="M" theme="secondary">
                Upgrade plan
              </AnchorButton>
            }
            dismissable={false}
          />
        </div>
      )}
    </div>
  );
};
